import type { ElevationRequest, PermissionPreset } from '@/api/types';

import { presetDisplayName } from '../presetDisplayName';

export type ElevationHistorySortColumn =
  'requestedAt' | 'decidedAt' | 'expiresAt' | 'status' | 'requester' | 'preset';

export type ElevationHistorySortDirection = 'ascending' | 'descending';

export interface ElevationHistorySort {
  column: ElevationHistorySortColumn;
  direction: ElevationHistorySortDirection;
}

export const DEFAULT_ELEVATION_HISTORY_SORT: ElevationHistorySort = {
  column: 'requestedAt',
  direction: 'descending',
};

function compareNullableNumbers(left: number | null, right: number | null): number {
  if (left === null) return right === null ? 0 : 1;
  if (right === null) return -1;
  return left - right;
}

export function sortElevationHistory(
  requests: ElevationRequest[],
  sort: ElevationHistorySort,
  presets: PermissionPreset[] | undefined,
): ElevationRequest[] {
  return [...requests].sort((left, right) => {
    let comparison: number;

    switch (sort.column) {
      case 'requestedAt':
        comparison = left.createdAt - right.createdAt;
        break;
      case 'decidedAt':
        comparison = compareNullableNumbers(left.decidedAt, right.decidedAt);
        if (left.decidedAt === null || right.decidedAt === null) return comparison;
        break;
      case 'expiresAt':
        comparison = compareNullableNumbers(left.expiresAt, right.expiresAt);
        if (left.expiresAt === null || right.expiresAt === null) return comparison;
        break;
      case 'status':
        comparison = left.status.localeCompare(right.status);
        break;
      case 'requester':
        comparison = left.requesterUpn.localeCompare(right.requesterUpn);
        break;
      case 'preset':
        comparison = presetDisplayName(left.presetKey, presets).localeCompare(
          presetDisplayName(right.presetKey, presets),
        );
        break;
    }

    if (comparison === 0) return right.createdAt - left.createdAt;

    return sort.direction === 'ascending' ? comparison : -comparison;
  });
}
